import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { gql } from '@apollo/client';
import { getApolloClient } from 'utils/apolloUtils';
import { getApolloClientMfe } from 'utils/apolloUtilsMfe';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import queryService from '../../v2/services/queryService';
import { constants } from 'constants/Constants';
import { getKeyByValue } from '../../v2/utils/helper';
import store from '../../store/store';

export function useDynamicGraphQLQuery(
  queryName,
  customFilters = {},
  initialFetch = true,
  customFolderPath = null,
  fetchPolicy = 'cache-first'
) {
  const isMFE = store.getState().app.isMFE;
  const client = isMFE ? getApolloClientMfe() : getApolloClient();
  const programsMapping = constants.programs;
  const productsMapping = constants.products;
  const { ...contextFilters } = useContext(FilterContext);

  // Normalize queryName into an array.
  const queryNames = Array.isArray(queryName) ? queryName : [queryName];

  // Global combined filters.
  const globalCombinedFilters = {
    ...contextFilters,
    ...customFilters,
  };

  // Determine folder names from context or use custom folder path if provided
  let folderPath;
  if (customFolderPath) {
    // Use the custom folder path directly
    folderPath = customFolderPath;
  } else {
    // Use the default program/product folder structure
    const programFolder =
      contextFilters.selectedProgram?.programId &&
      getKeyByValue(programsMapping, contextFilters.selectedProgram.programId)
        ? getKeyByValue(
            programsMapping,
            contextFilters.selectedProgram.programId
          )
        : 'defaultProgram';
    const productFolder =
      contextFilters.selectedProduct?.productId &&
      getKeyByValue(productsMapping, contextFilters.selectedProduct.productId)
        ? getKeyByValue(
            productsMapping,
            contextFilters.selectedProduct.productId
          )
        : 'defaultProduct';

    folderPath = `${programFolder}/${productFolder}`;
  }

  // --- Conditional Fetch Flag ---
  const [shouldFetch, setShouldFetch] = useState(initialFetch);

  // --- State for JSON configurations ---
  const [queryJsons, setQueryJsons] = useState(null);

  // Build dependency filters: only those mapped by param names in queryJsons (via queryService) or custom filters
  let filteredDependencyStr = '';
  if (
    queryJsons &&
    typeof queryJsons === 'object' &&
    Object.keys(queryJsons).length > 0
  ) {
    const dependencyKeys = new Set();
    Object.values(queryJsons).forEach((qJson) => {
      if (qJson && Array.isArray(qJson.params)) {
        qJson.params.forEach((param) => {
          const serviceDef = queryService[param];
          if (serviceDef && Array.isArray(serviceDef.params)) {
            serviceDef.params.forEach((depKey) => dependencyKeys.add(depKey));
          }
        });
      }
    });
    let dependencyFilters = {};
    dependencyKeys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(globalCombinedFilters, key)) {
        dependencyFilters[key] = globalCombinedFilters[key];
      }
    });
    // Always include custom filters
    dependencyFilters = { ...dependencyFilters, ...customFilters };
    filteredDependencyStr = JSON.stringify(dependencyFilters);
  }
  const [jsonLoading, setJsonLoading] = useState(true);

  // --- States for query responses & errors ---
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [queriesLoading, setQueriesLoading] = useState(true);

  // --- Refetch Mechanism ---
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const refetch = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  // --- Instance-specific AbortController for cleanup only ---
  const currentAbortControllerRef = useRef(null);

  // --- 1. Load Query JSON Configurations ---
  useEffect(() => {
    if (!shouldFetch) {
      return;
    }
    setJsonLoading(true);
    setQueryJsons(null);

    // Use require.context to load JSON configuration files.
    const context = require.context('../../graphql/config', true, /\.json$/);

    const importPromises = queryNames.map((qName) => {
      const relativePath = `./${folderPath}/${qName}.json`;
      return new Promise((resolve, reject) => {
        try {
          const module = context(relativePath);
          resolve({ queryName: qName, queryJson: module.default || module });
        } catch (err) {
          reject(err);
        }
      });
    });

    Promise.all(importPromises)
      .then((results) => {
        const queriesObj = results.reduce((acc, { queryName, queryJson }) => {
          acc[queryName] = queryJson;
          return acc;
        }, {});
        setQueryJsons(queriesObj);
        setJsonLoading(false);
      })
      .catch((err) => {
        setError([err]);
        setJsonLoading(false);
      });
  }, [shouldFetch, folderPath, queryNames.join(',')]);

  // --- 2. Execute the GraphQL Queries and Consolidate Responses ---
  useEffect(() => {
    if (!shouldFetch) {
      return;
    }
    if (!jsonLoading && queryJsons) {
      // Create new AbortController for this request
      const newAbortController = new AbortController();
      currentAbortControllerRef.current = newAbortController;

      setData(null);
      setError(null);
      setQueriesLoading(true);

      const queryPromises = Object.entries(queryJsons).map(([qName, qJson]) => {
        const { query, params } = qJson;
        if (!query) {
          return Promise.reject(
            new Error(
              `Invalid query JSON structure for ${qName}. Expected 'query'.`
            )
          );
        }
        let gqlQuery;
        try {
          gqlQuery = gql`
            ${query}
          `;
        } catch (err) {
          return Promise.reject(err);
        }

        const combinedQueryFilters = {
          ...contextFilters,
          ...customFilters,
        };

        let variables = {};
        if (params && Array.isArray(params)) {
          if (params.length > 0) {
            params.forEach((paramName) => {
              const serviceDef = queryService[paramName];
              if (
                serviceDef &&
                typeof serviceDef.paramFunction === 'function'
              ) {
                variables[paramName] =
                  serviceDef.paramFunction(combinedQueryFilters);
                variables[paramName] = {
                  ...variables[paramName],
                  ...customFilters,
                };
              }
            });
          } else {
            variables = { ...customFilters };
          }
        } else {
          variables = { ...customFilters };
        }

        return client.query({
          query: gqlQuery,
          variables,
          fetchPolicy: fetchPolicy,
          context: {
            fetchOptions: {
              signal: newAbortController.signal,
            },
          },
        });
      });

      Promise.allSettled(queryPromises).then((results) => {
        // Check if request was aborted
        if (newAbortController.signal.aborted) {
          return;
        }

        const finalData = {};
        const finalErrors = [];
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const res = result.value;
            if (res.errors && res.errors.length > 0) {
              finalErrors.push(...res.errors);
            }
            if (res.data) {
              Object.assign(finalData, res.data);
            }
          } else {
            // Don't add abortion errors to finalErrors
            if (result.reason?.name !== 'AbortError') {
              finalErrors.push(result.reason);
            }
          }
        });

        // Only update state if request wasn't aborted
        if (!newAbortController.signal.aborted) {
          setData(Object.keys(finalData).length > 0 ? finalData : null);
          setError(finalErrors.length > 0 ? finalErrors : null);
          setQueriesLoading(false);
        }
        // Clear the current controller reference when request completes
        currentAbortControllerRef.current = null;
      });
    }
  }, [
    shouldFetch,
    jsonLoading,
    queryJsons,
    client,
    filteredDependencyStr,
    refetchTrigger,
  ]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (currentAbortControllerRef.current) {
        currentAbortControllerRef.current.abort();
        currentAbortControllerRef.current = null;
      }
    };
  }, []);

  // Overall loading is true if either JSON config or query execution is still in progress.
  const isLoading = jsonLoading || queriesLoading;

  return { data, isLoading, error, refetch, setShouldFetch };
}
